import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { CategoryChips, OppScore, ScoreBreakdown, MiniMetric } from "@/components/opportunity";
import { ArrowLeft, ExternalLink, Lightbulb, PenLine } from "lucide-react";
import { toast } from "sonner";

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null });
  const [opp, setOpp] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [detail, ev] = await Promise.all([
        api.get(`/opportunities/${id}`),
        api.get(`/opportunities/${id}/evidence`),
      ]);
      setOpp(detail.data);
      setEvidence(ev.data.evidence || []);
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: apiErrorMessage(err) });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const createContent = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/opportunities/${id}/create-content`, { title: opp.title });
      toast.success("Content project created");
      navigate(`/ideas?opportunity=${id}`);
      return data;
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  if (state.loading) return <AppShell title="Opportunities"><Loader label="Loading opportunity detail" /></AppShell>;
  if (state.error)   return <AppShell title="Opportunities"><ErrorState message={state.error} onRetry={load} /></AppShell>;

  return (
    <AppShell title="Opportunity Detail">
      {/* Back */}
      <div className="mb-5">
        <Link to="/opportunities" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" /> All opportunities
        </Link>
      </div>

      <div className="space-y-6">
        {/* Header card */}
        <section className="rounded-lg border border-border bg-surface p-4 sm:p-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <CategoryChips categories={opp.categories} />
              <h1 className="mt-3 font-display text-2xl font-bold text-foreground sm:text-3xl">
                {opp.title}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {opp.positioning || opp.recommended_angle}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Ranked by your Creator DNA and real evidence.
              </p>
            </div>
            <div className="shrink-0">
              <OppScore score={opp.opportunity_score} confidence={opp.confidence_score} />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button onClick={createContent} disabled={creating} className="bg-accent text-accent-foreground hover:bg-accent-hover">
              <PenLine className="mr-2 h-4 w-4" />
              {creating ? "Creating…" : "Create content"}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/ideas?opportunity=${id}`)}>
              <Lightbulb className="mr-2 h-4 w-4" /> Generate ideas
            </Button>
          </div>
        </section>

        {/* Main 2-col layout: stacks on mobile, side-by-side md+ */}
        <div className="grid gap-6 md:grid-cols-[2fr_1fr]">
          {/* Main content */}
          <section className="space-y-4">
            <Info title="Why this opportunity exists">
              <p>{opp.problem || "A recurring need is visible in the latest Intelligence signals."}</p>
              <p>{opp.audience_need}</p>
              <p>{opp.why_now}</p>
              <p>{opp.why_creator}</p>
            </Info>

            <Info title="Recommended positioning">
              <p>{opp.positioning || opp.recommended_angle}</p>
              <p className="mt-3">Format: {opp.recommended_format || "Flexible educational format"}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(opp.recommended_formats || []).map((x) => (
                  <span key={x} className="rounded-md bg-elevated px-2 py-1 text-xs text-muted-foreground">{x}</span>
                ))}
              </div>
            </Info>

            <Info title={`Market evidence (${evidence.length})`}>
              <div className="space-y-3">
                {evidence.map((item) => (
                  <a key={item.id} href={item.url} target="_blank" rel="noreferrer"
                    className="flex items-start justify-between gap-3 rounded-md border border-border p-3 hover:bg-elevated">
                    <span>
                      <span className="block text-sm font-medium text-foreground">{item.title || item.source}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {item.source} {item.author ? `· ${item.author}` : ""}
                      </span>
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </Info>
          </section>

          {/* Aside */}
          <aside className="space-y-4">
            <section className="rounded-lg border border-border bg-surface p-5">
              <h2 className="font-display text-base font-semibold text-foreground">Score breakdown</h2>
              <div className="mt-4">
                <ScoreBreakdown scores={opp.scores} />
              </div>
            </section>

            <section className="grid grid-cols-2 gap-3">
              <MiniMetric label="Sources" value={opp.source_count} />
              <MiniMetric label="Evidence" value={opp.evidence_item_ids?.length || 0} />
            </section>

            <section className="rounded-lg border border-border bg-surface p-5">
              <h2 className="font-display text-base font-semibold text-foreground">Linked gaps</h2>
              {opp.linked_gaps?.length ? (
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  {opp.linked_gaps.map((g) => <li key={g}>• {g}</li>)}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">No additional gap signals linked.</p>
              )}
            </section>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

function Info({ title, children }) {
  return (
    <section className="rounded-lg border border-border bg-surface p-5">
      <h2 className="font-display text-base font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
